import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Table, Button, Space, Typography, Alert, Upload, Modal, Tag, message } from 'antd';
import { CloudDownloadOutlined, UploadOutlined, DatabaseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import DataTransfer from '../../components/DataTransfer';
import dataService from '../../services/dataService';

const { Text, Paragraph } = Typography;

// Every section a doctor can move in and out of the app, plus the one button that
// pulls the lot onto their own computer.
//
// The section list comes from the server rather than being repeated here, so a
// section added to the backend appears on this page without touching it.
const DataBackup = () => {
    const { t } = useTranslation();
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [backingUp, setBackingUp] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [restoreResults, setRestoreResults] = useState(null);

    useEffect(() => {
        fetchSections();
    }, []);

    const fetchSections = async () => {
        setLoading(true);
        try {
            const response = await dataService.getSections();
            setSections(response.data || []);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    const handleBackup = async () => {
        setBackingUp(true);
        try {
            await dataService.downloadBackup();
            message.success(t('backupDownloaded'));
        } catch (error) {
            message.error(error.message || t('error'));
        } finally {
            setBackingUp(false);
        }
    };

    const handleRestore = async (file) => {
        setRestoring(true);
        try {
            const response = await dataService.importBackup(file);
            setRestoreResults(response.data || []);
        } catch (error) {
            message.error(error.response?.data?.message || error.message || t('error'));
        } finally {
            setRestoring(false);
        }
        return false;
    };

    const columns = [
        {
            title: t('section'),
            key: 'section',
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{t(record.labelKey) || record.sheet}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('sheetName')}: {record.sheet}
                    </Text>
                </Space>
            )
        },
        {
            title: t('requiredColumns'),
            key: 'required',
            render: (_, record) => {
                const required = record.columns.filter((column) => column.required);
                return required.length > 0 ? (
                    <Space size={4} wrap>
                        {required.map((column) => <Tag key={column.header}>{column.header}</Tag>)}
                    </Space>
                ) : <Text type="secondary">-</Text>;
            }
        },
        {
            title: t('columns'),
            key: 'count',
            width: 100,
            align: 'center',
            render: (_, record) => record.columns.length
        },
        {
            title: t('actions'),
            key: 'actions',
            width: 380,
            render: (_, record) => <DataTransfer section={record.key} size="small" />
        }
    ];

    return (
        <div className="page-container">
            <Row gutter={[16, 16]} align="middle" className="page-header">
                <Col xs={24}>
                    <h1 className="page-title">{t('dataAndBackup')}</h1>
                </Col>
            </Row>

            <Card style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]} align="middle" justify="space-between">
                    <Col xs={24} md={14}>
                        <Space direction="vertical" size={4}>
                            <Text strong><DatabaseOutlined /> {t('backupAllData')}</Text>
                            <Text type="secondary">{t('backupHint')}</Text>
                        </Space>
                    </Col>
                    <Col xs={24} md={10} style={{ textAlign: 'right' }}>
                        <Space wrap>
                            <Button
                                type="primary"
                                size="large"
                                icon={<CloudDownloadOutlined />}
                                loading={backingUp}
                                onClick={handleBackup}
                            >
                                {t('downloadBackup')}
                            </Button>
                            <Upload accept=".xlsx" showUploadList={false} beforeUpload={handleRestore}>
                                <Button size="large" icon={<UploadOutlined />} loading={restoring}>
                                    {t('restoreBackup')}
                                </Button>
                            </Upload>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('importHowTo')}
                description={
                    <Paragraph style={{ marginBottom: 0 }}>
                        {t('importHowToDetail')}
                    </Paragraph>
                }
            />

            <Card className="table-card">
                <Table
                    columns={columns}
                    dataSource={sections}
                    rowKey="key"
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 900 }}
                />
            </Card>

            <Modal
                open={Boolean(restoreResults)}
                title={t('importResult')}
                onCancel={() => setRestoreResults(null)}
                onOk={() => setRestoreResults(null)}
                cancelButtonProps={{ style: { display: 'none' } }}
                width={760}
            >
                <Table
                    size="small"
                    rowKey="section"
                    dataSource={restoreResults || []}
                    pagination={false}
                    columns={[
                        { title: t('section'), dataIndex: 'sheet', key: 'sheet' },
                        { title: t('rowsRead'), dataIndex: 'read', key: 'read', align: 'center' },
                        { title: t('created'), dataIndex: 'created', key: 'created', align: 'center' },
                        { title: t('updated'), dataIndex: 'updated', key: 'updated', align: 'center' },
                        {
                            title: t('failed'),
                            dataIndex: 'failed',
                            key: 'failed',
                            align: 'center',
                            render: (value) => (value > 0 ? <Text type="danger">{value}</Text> : value)
                        }
                    ]}
                    expandable={{
                        // Only a section that actually rejected rows opens, and it opens
                        // onto the row numbers as they appear in the doctor's spreadsheet.
                        rowExpandable: (record) => record.errors?.length > 0,
                        expandedRowRender: (record) => (
                            <Table
                                size="small"
                                rowKey={(row) => `${row.row}-${row.message}`}
                                dataSource={record.errors}
                                pagination={record.errors.length > 8 ? { pageSize: 8 } : false}
                                columns={[
                                    { title: t('rowNumber'), dataIndex: 'row', key: 'row', width: 90 },
                                    { title: t('problem'), dataIndex: 'message', key: 'message' }
                                ]}
                            />
                        )
                    }}
                />
            </Modal>
        </div>
    );
};

export default DataBackup;
